// frontend/src/components/Auth/VerifyEmail.jsx - VERSION CORRIGÉE

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Spinner, Alert, Button } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import api from '../../services/api';

const VerifyEmail = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading');
    const [message, setMessage] = useState('');

    // Utiliser useCallback pour éviter le warning
    const verifyEmail = useCallback(async () => {
        try {
            const response = await api.get(`/email/verify/${token}`);

            if (response.data.already_verified) {
                setStatus('already_verified');
                setMessage('Votre email est déjà vérifié');
            } else {
                setStatus('success');
                setMessage(response.data.message);
            }
        } catch (error) {
            if (error.response?.data?.error?.includes('expiré') || error.response?.data?.error?.includes('expired')) {
                setStatus('expired');
                setMessage(error.response.data.error);
            } else {
                setStatus('error');
                setMessage(error.response?.data?.error || 'Erreur lors de la vérification');
            }
        }
    }, [token]); // Dépendance sur token

    useEffect(() => {
        verifyEmail();
    }, [verifyEmail]); // Dépendance sur verifyEmail

    return (
        <div className="auth-page">
            <Container className="py-5">
                <Card className="text-center shadow-lg" style={{ maxWidth: '600px', margin: '0 auto' }}>
                    <Card.Body className="p-5">
                        {status === 'loading' && (
                            <>
                                <Spinner animation="border" variant="primary" className="mb-4" style={{ width: '60px', height: '60px' }} />
                                <h3>Vérification en cours...</h3>
                                <p className="text-muted">Veuillez patienter quelques instants</p>
                            </>
                        )}

                        {status === 'success' && (
                            <>
                                <FaCheckCircle size={80} className="text-success mb-4" />
                                <h2 className="mb-3">Email vérifié avec succès !</h2>
                                <Alert variant="success" className="mb-4">
                                    <p className="mb-0">{message}</p>
                                </Alert>
                                <p className="text-muted mb-4">
                                    Votre compte est maintenant activé. Vous pouvez vous connecter et commencer à utiliser nos services.
                                </p>
                                <Button 
                                    variant="primary" 
                                    size="lg"
                                    onClick={() => navigate('/login')}
                                    className="px-5"
                                >
                                    Se connecter maintenant
                                </Button>
                            </>
                        )}

                        {status === 'already_verified' && (
                            <>
                                <FaCheckCircle size={80} className="text-info mb-4" />
                                <h2 className="mb-3">Email déjà vérifié</h2>
                                <Alert variant="info" className="mb-4">
                                    <p className="mb-0">{message}</p>
                                </Alert>
                                <p className="text-muted mb-4">
                                    Votre compte est déjà actif. Connectez-vous pour accéder à votre espace.
                                </p>
                                <Button 
                                    variant="primary" 
                                    size="lg"
                                    onClick={() => navigate('/login')}
                                    className="px-5"
                                >
                                    Se connecter
                                </Button>
                            </>
                        )}

                        {status === 'expired' && (
                            <>
                                <FaClock size={80} className="text-warning mb-4" />
                                <h2 className="mb-3">Lien expiré</h2>
                                <Alert variant="warning" className="mb-4">
                                    <FaExclamationTriangle className="me-2" />
                                    {message}
                                </Alert>
                                <p className="text-muted mb-4">
                                    Le lien de vérification a expiré après 24 heures. Demandez un nouveau lien pour activer votre compte.
                                </p>
                                <div className="d-flex gap-2 justify-content-center">
                                    <Link to="/resend-verification" className="btn btn-primary btn-lg px-4">
                                        Renvoyer l'email
                                    </Link>
                                    <Button 
                                        variant="outline-secondary"
                                        size="lg"
                                        onClick={() => navigate('/login')}
                                    >
                                        Connexion
                                    </Button>
                                </div>
                            </>
                        )}

                        {status === 'error' && (
                            <>
                                <FaTimesCircle size={80} className="text-danger mb-4" />
                                <h2 className="mb-3">Erreur de vérification</h2>
                                <Alert variant="danger" className="mb-4">
                                    <strong>Erreur:</strong> {message}
                                </Alert>
                                <p className="text-muted mb-4">
                                    Le lien de vérification est invalide ou a déjà été utilisé.
                                </p>
                                <div className="d-flex gap-2 justify-content-center">
                                    <Link to="/resend-verification" className="btn btn-primary">
                                        Renvoyer l'email
                                    </Link>
                                    <Button 
                                        variant="outline-secondary"
                                        onClick={() => navigate('/login')}
                                    >
                                        Retour à la connexion
                                    </Button>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>

                <div className="text-center mt-4">
                    <p className="text-muted">
                        Besoin d'aide ? <Link to="/support">Contactez le support</Link>
                    </p>
                </div>
            </Container>
        </div>
    );
};

export default VerifyEmail;